import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import config from './config'
import './assets/fonts.css'
import './assets/main.css'

// Validate required environment variables
const requiredEnvVars = {
  'VITE_API_BASE_URL': config.apiBaseURL,
  'VITE_SOCKET_URL': config.socketURL,
  'VITE_GOOGLE_MAPS_API_KEY': config.googleMapsAPIKey,
  'VITE_OSRM_URL': config.osrmURL,
  'VITE_IDYANGU_URL': config.idyanguURL
};

const missingVars = Object.entries(requiredEnvVars)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingVars.length > 0) {
  console.error('Missing required environment variables:');
  missingVars.forEach(v => console.error(`   - ${v}`));
  console.error('\nPlease configure these variables in .env before starting');
  // Don't throw, but log prominently
  console.warn('Application may not work correctly without these configurations');
}

// Vite adds <link rel="modulepreload"> for likely-needed chunks. If a deploy has replaced the
// build since this tab loaded, those hashed filenames no longer exist on the server and the
// preload fails independently of any user navigation. Vite fires this event so we can recover
// with a full reload — reusing router.onError's guard key avoids fighting over the same reload.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  const reloadKey = 'sbt-chunk-reload-attempted'
  if (sessionStorage.getItem(reloadKey) === window.location.pathname) {
    console.error('Module preload still failing after a reload — not retrying again')
    return
  }
  sessionStorage.setItem(reloadKey, window.location.pathname)
  window.location.reload()
})

const app = createApp(App)
const pinia = createPinia()

// Initialize Pinia first
app.use(pinia)

// Then use router (which depends on stores)
app.use(router)

// Finally mount the app
app.mount('#app')

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for updates
      })
      
      console.log('Service Worker registered successfully:', registration.scope)
      
      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          console.log('New Service Worker installing...')
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // New update available
                console.log('New version available! User can refresh to update.')
                // Don't auto-prompt - just log it. User can refresh manually or close/reopen app
              } else {
                // First install
                console.log('App is cached and ready for offline use!')
              }
            }
          })
        }
      })
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('Message from Service Worker:', event.data)
        
        if (event.data && event.data.type === 'CACHE_UPDATED') {
          console.log('Cache updated, new content available')
        }
      })
      
    } catch (error) {
      console.error('Service Worker registration failed:', error)
    }
  })
  
  // Don't automatically reload on controller change - let the app handle updates gracefully
  // navigator.serviceWorker.addEventListener('controllerchange', () => {
  //   console.log('Service Worker controller changed')
  //   // Only reload if we're not already on offline page
  //   if (!window.location.pathname.includes('offline')) {
  //     window.location.reload()
  //   }
  // })
}

// PWA Install Prompt Handler
let deferredPrompt: any = null

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('PWA install prompt available')
  e.preventDefault()
  deferredPrompt = e
  
  // Optionally show custom install button
  showInstallButton()
})

function showInstallButton() {
  // You can add UI to show install button here
  // For example, add to app header or show as a banner
  console.log('App can be installed as PWA')
}

// Handle PWA installation
window.addEventListener('appinstalled', () => {
  console.log('PWA installed successfully!')
  deferredPrompt = null
  
  // Track installation analytics if needed
  // analytics.track('pwa_installed')
})

// Expose install function globally for use in components
declare global {
  interface Window {
    installPWA: () => Promise<void>
  }
}

window.installPWA = async () => {
  if (deferredPrompt) {
    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        console.log('User accepted PWA install')
      } else {
        console.log('User dismissed PWA install')
      }
      
      deferredPrompt = null
    } catch (error) {
      console.error('PWA install error:', error)
    }
  } else {
    console.log('❓ PWA install not available')
  }
}

